/**
 * NCommentsSelector Component
 *
 * Number input for configuring the N Comments target count.
 * Shows default hint and allows custom value within valid range.
 *
 * Feature: 651-n-comments-task
 */

import { useCallback, useEffect, useMemo } from 'react';
import { calculateDefaultNComments } from '@/types/photo-rating';

export interface NCommentsSelectorProps {
  /** Current target count value */
  value: number;
  /**
   * Callback when value changes
   * NOTE: This callback should be memoized (useCallback) to prevent infinite re-renders
   */
  onChange: (value: number) => void;
  /** Number of photos in the collection (for default calculation and max) */
  photoCount: number;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
}

/**
 * Selector for N Comments target count
 *
 * Displays the default hint and allows the photographer to set a custom value.
 */
export function NCommentsSelector({
  value,
  onChange,
  photoCount,
  disabled = false,
  error,
}: NCommentsSelectorProps) {
  // Defensive: calculate default N with error handling
  // Memoize to prevent recalculation on every render
  const defaultN = useMemo(() => {
    try {
      return calculateDefaultNComments(photoCount);
    } catch {
      // Fallback to 1 if photoCount is invalid
      return 1;
    }
  }, [photoCount]);

  // Safe photoCount for validation (ensure at least 1)
  const safePhotoCount = Math.max(1, photoCount);

  // Set default value when mounted if value is 0 or undefined
  useEffect(() => {
    if (!value || value <= 0) {
      onChange(defaultN);
    }
  }, [value, defaultN, onChange]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(e.target.value, 10);
      if (!isNaN(newValue)) {
        // Clamp to valid range
        const clampedValue = Math.min(Math.max(1, newValue), safePhotoCount);
        onChange(clampedValue);
      }
    },
    [onChange, safePhotoCount]
  );

  const handleBlur = useCallback(() => {
    // Ensure value is valid on blur
    if (!value || value < 1) {
      onChange(1);
    } else if (value > safePhotoCount) {
      onChange(safePhotoCount);
    }
  }, [value, onChange, safePhotoCount]);

  const isDefault = value === defaultN;

  return (
    <div className="mt-3 ml-9 p-3 bg-muted/50 rounded-md border border-border/50">
      <div className="flex items-center gap-3">
        <label
          htmlFor="n-comments-target"
          className="text-sm text-foreground whitespace-nowrap"
        >
          Ask for comments on
        </label>
        <input
          id="n-comments-target"
          type="number"
          min={1}
          max={safePhotoCount}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          className="w-16 px-2 py-1 text-center border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          aria-describedby="n-comments-hint"
        />
        <span className="text-sm text-foreground">
          of {safePhotoCount} photos
        </span>
      </div>
      <p
        id="n-comments-hint"
        className="mt-2 text-xs text-muted-foreground"
      >
        {isDefault ? (
          <>Default: {defaultN} (1/3 of photos)</>
        ) : (
          <>Default would be {defaultN} (1/3 of photos)</>
        )}
      </p>
      {error && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

NCommentsSelector.displayName = 'NCommentsSelector';
